// Injection point — the ONLY app module that imports the real @ng-org SDK, to
// inject it into @ng-eventually/polyfill. Every other Festipod module gets its
// NextGraph surface from @ng-eventually/polyfill. Removed at migration.
import { ng as realNg, init as realInit } from "@ng-org/web";
import type { NG } from "@ng-eventually/polyfill";
import { initNg as realInitNg } from "@ng-org/orm";
import { useShape as realUseShape } from "@ng-org/orm/react";

// SDK-shaped surface used by ngSession itself — taken from the SDK, not @ng-org.
import { configure, ng, init as initNgWeb, initNg as initNgSignals } from "@ng-eventually/polyfill";
// The deployment's wallet — ONE copy of these values in the app, shared with the
// access barrier (modules/auth re-exports this module).
import {
    SHARED_WALLET_FILE_URL,
    SHARED_WALLET_PASSWORD,
    WALLET_IMPORT_URL,
    hasSharedWallet,
} from "./sharedWallet";

// DIAGNOSTIC (shared-wallet isolation): the SDK access log SEES every read/write
// prefixed by the active identity — the way to catch a doc read under the wrong
// identity in the REAL app. Now ON BY DEFAULT (the opt-in toggle was fragile /
// easy to miss). To silence: in the browser console run
//   localStorage.setItem('festipod.debug.accessLog','0')   // then reload
function accessLogEnabled(): boolean {
    try {
        if (typeof localStorage !== "undefined"
            && localStorage.getItem("festipod.debug.accessLog") === "0") return false;
    } catch { /* storage may be unavailable */ }
    return true;
}

export let session: NextGraphSession | undefined;

let resolveSessionPromise: (
    value: NextGraphSession | PromiseLike<NextGraphSession>
) => void;
let rejectSessionPromise: (reason?: any) => void;

export let sessionPromise: Promise<NextGraphSession> = new Promise(
    (resolve, reject) => {
        resolveSessionPromise = resolve;
        rejectSessionPromise = reject;
    }
);

// --- The ONE bootstrap call -------------------------------------------------
// `configure` is the single bootstrap entry, and it now injects ONE thing: the
// real @ng-org surface plus what this deployment hands its users. It carries no
// consumer wiring any more — THE SESSION IS THE SDK'S, not ours. We never build
// one and no call takes one: the SDK captures the session itself when `init` (its
// own, called below) fires, and it normalizes identities itself. The app reads
// the `session_id` off the same `init` event, for the `docs` primitives only.
const __accessLogOn = accessLogEnabled();
console.log("[NG session] access-log:", __accessLogOn ? "ON" : "OFF (localStorage festipod.debug.accessLog=0)");
console.log("[NG session] shared wallet:", hasSharedWallet() ? "configured" : "absent");
configure({
    ng: realNg, useShape: realUseShape, init: realInit, initNg: realInitNg,
    debugAccessLog: __accessLogOn,
    // What THIS deployment hands its users: the wallet file it serves and the
    // password it publishes. The SDK reads no environment variable — the app
    // resolves the values (build-time `define` or the runtime config endpoint)
    // and passes them. No shared wallet configured → nothing to pass.
    sharedWallet: hasSharedWallet()
        ? {
            fileUrl: SHARED_WALLET_FILE_URL,
            password: SHARED_WALLET_PASSWORD,
            importUrl: WALLET_IMPORT_URL,
        }
        : undefined,
});

let initPromise: Promise<void> | null = null;

/**
 * START THE SESSION — the SDK's own `init`, which is the ONLY way a session comes
 * into being. It captures the session itself and then calls this callback with the
 * same event untouched; the app reads `session_id` off it, because the `docs`
 * primitives are the one part of the surface that still names a session.
 *
 * ORDER IS CONTRACTUAL: this call must have happened before anything awaits
 * `ensureIdentity()`, which throws otherwise. Idempotent — repeated calls return
 * the same promise, so every entry point may (and should) call it before awaiting.
 */
export function startNgSession(): Promise<void> {
    if (initPromise) return initPromise;
    console.log('[NG session] startNgSession() — registering the init callback');
    initPromise = (initNgWeb(
        async (event: any) => {
            session = event.session;
            session!.ng ??= realNg;
            console.log('[NG session] Connected');
            resolveSessionPromise(session!);
            initNgSignals(realNg, session!);
        },
        true,
        []
    ) as Promise<void>).catch((error: any) => {
        console.error('[NG session] init error:', error);
        rejectSessionPromise(error);
    });
    return initPromise;
}

// NO `login()` HERE ANY MORE. Opening the wallet page is not something the app
// drives: `ensureIdentity()` mounts its own barrier and owns the round-trip that
// follows. Nothing called this, and the surface publishes no such call.

/**
 * REAL NextGraph logout — stops the session of the SHARED wallet.
 *
 * STOPGAP: must stay HIDDEN (Settings/debug only). Calling this forces a new
 * broker redirect on the next access — see
 * decision_2026-06-15_shared-wallet-login-flow.
 */
export async function logoutNg(): Promise<void> {
    // `session.user` comes through the event's open index signature, so narrow it
    // rather than assert it: `session_stop` names a user id, and there is nothing
    // to stop if the session never carried one.
    const userId = session?.user;
    if (typeof userId !== 'string' || !userId) return;
    try {
        await ng.session_stop(userId);
        console.log('[NG session] session_stop done');
    } catch (error) {
        console.error('[NG session] logout error:', error);
    }
}

export interface NextGraphSession {
    // `NG` IS the type of the SDK object (re-exported by @ng-eventually/polyfill) —
    // not a value whose type we could take. `typeof NG` was a type-level error that
    // the broken typecheck gate hid.
    ng: NG;
    // RELAYED, never converted: the `docs` primitives declare `string | number`
    // because that is what a session carries, and handing them a stringified one
    // fails. Declaring it `string` here would invite exactly that coercion.
    session_id: string | number;
    protected_store_id: string;
    private_store_id: string;
    public_store_id: string;
    [key: string]: unknown;
}
